import { CombinedManualAdjustmentType, combinedManualAdjustmentTypes } from '@aloft/toolkit-shared';
import { Block, Button, Flex, IconButton, SimpleSelect, Typography } from '@aloft/toolkit-sui';

import { difference } from 'lodash';
import { useFieldArray } from 'react-hook-form';

import { useZodFormContext } from '../../hooks/useZodForm.ts';
import { ADJUSTMENT_LABELS } from '../../modules/adjustments/internal/PairedCostSettings/internal/consts.ts';
import { isValidAdjForFormType } from '../../modules/adjustments/internal/PairedCostSettings/internal/isValidAdjForFormType.ts';
import { isValidAdjForVersion } from '../../modules/adjustments/internal/PairedCostSettings/internal/isValidAdjForVersion.ts';
import { selectFormType } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { RoundingSelect } from './RoundingSelect.tsx';
import { ThresholdInput } from './ThresholdInput.tsx';
import { schSettingsForm } from './schSettingsForm.ts';

const pairedCostVersion = '2.0';
const exceptionDefaults: { rounding: 'roundingDefault'; threshold: 'thresholdDefault' } = {
  rounding: 'roundingDefault',
  threshold: 'thresholdDefault',
};

export const RoundingAndThresholdExceptions = ({
  exceptionType,
}: {
  exceptionType: keyof typeof exceptionDefaults;
}) => {
  const { register, watch } = useZodFormContext<typeof schSettingsForm>();
  const { fields, append, remove } = useFieldArray({
    name: exceptionType,
  });
  const formType = useAppSelector(selectFormType);
  const current = (watch(exceptionType) ?? []).map(
    (exception: { adjustmentType: CombinedManualAdjustmentType }) => exception.adjustmentType,
  );
  const currentDefault = watch(exceptionDefaults[exceptionType]) ?? ('' as string);
  const validAdjustmentTypes = combinedManualAdjustmentTypes.filter(
    (type) =>
      isValidAdjForVersion(type, pairedCostVersion) && isValidAdjForFormType(type, formType),
  );

  const addAdjustmentType = () => {
    const firstAvailable = difference(validAdjustmentTypes, current)[0];
    append({
      adjustmentType: firstAvailable as CombinedManualAdjustmentType,
      value: currentDefault,
    });
  };

  return (
    <Block marginTop="0.5rem">
      <Typography variant="h5">Except:</Typography>

      {fields.map((field, index) => (
        <Flex gap=".5rem" key={field.id} marginTop=".5rem">
          <SimpleSelect size="sm" {...register(`${exceptionType}.${index}.adjustmentType`)}>
            {validAdjustmentTypes.map((type) => (
              <option
                key={type}
                value={type}
                disabled={
                  type !==
                    // I gave up trying to figure out how to do this typing properly so this didn't get angry :/
                    (
                      field as unknown as {
                        adjustmentType: CombinedManualAdjustmentType;
                      }
                    ).adjustmentType && current.includes(type)
                }
              >
                {ADJUSTMENT_LABELS[type]}
              </option>
            ))}
          </SimpleSelect>
          {exceptionType === 'threshold' ? (
            <ThresholdInput fieldPath={`${exceptionType}.${index}.value`} />
          ) : (
            <RoundingSelect fieldPath={`${exceptionType}.${index}.value`} />
          )}
          <IconButton
            size="sm"
            variant="secondary"
            icon="delete-bin"
            onClick={() => remove(index)}
            ariaLabel="Delete"
          />
        </Flex>
      ))}
      <Block marginTop=".25rem">
        <Button size="sm" variant="inline" text="Add a custom rule" onClick={addAdjustmentType} />
      </Block>
    </Block>
  );
};
